import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { TrendingUp, CalendarCheck, Users, Ticket, Percent, Euro } from 'lucide-react';
import AdminSection from '../components/AdminSection';
import { fetchStats } from '../lib/adminApi';

const eur = (v) => new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(v || 0);
const shortDate = (d) => (d ? new Date(d).toLocaleDateString('es-ES', { day: '2-digit', month: 'short' }) : '—');

function Kpi({ icon: Icon, label, value, accent }) {
  return (
    <div className="rounded-[24px] border border-slate-200 bg-white p-5 shadow-[0_20px_60px_-40px_rgba(11,28,38,0.3)]">
      <div className="flex items-center gap-3">
        <div className={`flex h-11 w-11 items-center justify-center rounded-2xl ${accent || 'bg-[#f4fbfa] text-[#1fa5a3]'}`}>
          <Icon className="h-5 w-5" />
        </div>
        <div>
          <p className="text-2xl font-bold text-[#14213d]">{value}</p>
          <p className="text-xs uppercase tracking-[0.15em] text-slate-500">{label}</p>
        </div>
      </div>
    </div>
  );
}

// Minimal inline bar chart for the last ~14 days of revenue.
function RevenueChart({ data }) {
  const days = (data || []).slice(-14);
  if (!days.length) return <p className="py-8 text-center text-sm text-slate-400">Aún no hay ingresos que mostrar.</p>;
  const max = Math.max(...days.map((d) => d.revenue), 1);
  return (
    <div className="flex h-40 items-end gap-2">
      {days.map((d) => (
        <div key={d.date} className="group flex flex-1 flex-col items-center justify-end">
          <span className="mb-1 text-[10px] font-semibold text-[#1fa5a3] opacity-0 transition group-hover:opacity-100">{eur(d.revenue)}</span>
          <div className="w-full rounded-t-md bg-[#1fa5a3]/80 transition hover:bg-[#1fa5a3]" style={{ height: `${Math.max(4, (d.revenue / max) * 100)}%` }} />
          <span className="mt-1 text-[9px] text-slate-400">{shortDate(d.date)}</span>
        </div>
      ))}
    </div>
  );
}

export default function AdminDashboard() {
  const { data: s, isLoading } = useQuery({ queryKey: ['admin', 'stats'], queryFn: fetchStats });

  return (
    <AdminSection eyebrow="Admin" title="Panel de control" description="Resumen de ingresos, reservas y rendimiento de OWA." widthClassName="max-w-6xl">
      {isLoading ? <p className="text-sm text-slate-500">Cargando…</p> : null}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Kpi icon={Euro} label="Ingresos totales" value={eur(s?.revenue_total)} accent="bg-[#14213d] text-[#c8a25a]" />
        <Kpi icon={TrendingUp} label="Ingresos este mes" value={eur(s?.revenue_month)} />
        <Kpi icon={CalendarCheck} label="Reservas pagadas" value={s?.bookings_paid ?? '—'} />
        <Kpi icon={Users} label="Clientes" value={s?.customers ?? '—'} />
      </div>

      <div className="mt-4 grid gap-4 sm:grid-cols-3">
        <Kpi icon={Percent} label="Conversión (pagadas/creadas)" value={`${s?.conversion ?? 0}%`} />
        <Kpi icon={CalendarCheck} label="Reservas pendientes" value={s?.bookings_pending ?? '—'} />
        <Kpi icon={Ticket} label="Cupones usados" value={s?.coupons_used ?? 0} />
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-[1.2fr_0.8fr]">
        <section className="rounded-[28px] border border-slate-200 bg-white p-6 shadow-[0_28px_80px_-36px_rgba(11,28,38,0.24)]">
          <h2 className="mb-4 text-lg font-bold text-[#14213d]">Ingresos por día</h2>
          <RevenueChart data={s?.revenue_by_day} />
        </section>

        <section className="rounded-[28px] border border-slate-200 bg-white p-6 shadow-[0_28px_80px_-36px_rgba(11,28,38,0.24)]">
          <h2 className="mb-4 text-lg font-bold text-[#14213d]">Top actividades</h2>
          <div className="space-y-3">
            {(s?.top_activities || []).map((a, i) => (
              <div key={a.title} className="flex items-center gap-3">
                <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-[#f4fbfa] text-xs font-bold text-[#1fa5a3]">{i + 1}</span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-[#14213d]">{a.title}</p>
                  <p className="text-xs text-slate-500">{a.count} reservas</p>
                </div>
                <span className="text-sm font-bold text-[#1fa5a3]">{eur(a.revenue)}</span>
              </div>
            ))}
            {!s?.top_activities?.length ? <p className="py-6 text-center text-sm text-slate-400">Sin ventas todavía.</p> : null}
          </div>
        </section>
      </div>

      <section className="mt-6 rounded-[28px] border border-slate-200 bg-white p-6 shadow-[0_28px_80px_-36px_rgba(11,28,38,0.24)]">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-bold text-[#14213d]">Reservas recientes</h2>
          <Link to="/admin/reservations" className="text-sm font-semibold text-[#1fa5a3] hover:underline">Ver todas</Link>
        </div>
        <div className="divide-y divide-slate-100">
          {(s?.recent_bookings || []).map((b, i) => (
            <div key={i} className="flex items-center justify-between gap-3 py-2.5 text-sm">
              <div className="min-w-0">
                <span className="font-semibold text-[#14213d]">{b.activity}</span>
                <span className="text-slate-500"> · {b.customer || '—'}</span>
              </div>
              <div className="flex items-center gap-4 text-slate-500">
                <span>{shortDate(b.date)}</span>
                <span className="font-bold text-[#1fa5a3]">{eur(b.amount)}</span>
              </div>
            </div>
          ))}
          {!s?.recent_bookings?.length ? <p className="py-6 text-center text-sm text-slate-400">Sin reservas todavía.</p> : null}
        </div>
      </section>
    </AdminSection>
  );
}
